import { T_Coupon } from 'stores/coupon.store';

import { FilteredResults } from 'types/app.type';
import { productItem } from 'types/order.type';

import {
  ADDITIONAL_SKUS_LIST,
  BUNDLE_MOVIE_COUPON,
  CATEGORY,
  DEVICE_TYPE,
  HARDWARE,
  PACKAGES,
  PACKAGES_LIST,
  PRODUCT_SKU_PACKAGE_NAME,
  VoucherProductTypeEnums,
  productSkuIds,
  validCategories,
} from '@/lib/constants';

import { getDeviceType, productSkuIdList } from './productMapper';

// Plan Selection
export const getPackagesBasedOnCategory = (cartProducts: any, category: string) => {
  let packageList = [];
  if (category === CATEGORY.TV.toLocaleLowerCase()) {
    packageList = cartProducts.filter(
      (product: any) =>
        product.occurrenceNumber === null &&
        product.type === PACKAGES &&
        product.category.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()
    );
  } else if (category === CATEGORY.BROADBAND.toLocaleLowerCase()) {
    packageList = cartProducts.filter(
      (product: any) =>
        product.occurrenceNumber === null &&
        product.type === PACKAGES &&
        ![
          PACKAGES_LIST.BB_ADDONS,
          PACKAGES_LIST.BB_ADDONS_VOICE,
          PACKAGES_LIST.BB_UPGRADE,
        ].includes(product.subCategory)
    );
  } else if (category === CATEGORY.TV_BROADBAND.toLocaleLowerCase()) {
    packageList = [
      ...(cartProducts ?? []).filter(
        (product: any) =>
          product.occurrenceNumber === null &&
          product.type === PACKAGES &&
          product.category.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()
      ),
      ...(cartProducts ?? []).filter(
        (product: any) =>
          product.occurrenceNumber === null &&
          product.type === PACKAGES &&
          ![
            PACKAGES_LIST.BB_ADDONS,
            PACKAGES_LIST.BB_ADDONS_VOICE,
            PACKAGES_LIST.BB_UPGRADE,
          ].includes(product.subCategory) &&
          product.category.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()
      ),
    ];
  }
  return packageList;
};

// Device Selection

export const getDeviceBasedOnCategory = (productList: any, category: string) => {
  let deviceList = [];

  if (category === CATEGORY.TV_BROADBAND.toLocaleLowerCase()) {
    deviceList = productList?.filter(
      (it: any) =>
        it.type === HARDWARE &&
        [CATEGORY.TV.toLocaleLowerCase(), CATEGORY.BROADBAND.toLocaleLowerCase()].includes(
          it.categories.toLocaleLowerCase()
        )
    );
  } else if (validCategories.has(category)) {
    deviceList = productList?.filter(
      (it: any) => it.type === HARDWARE && it.categories.toLocaleLowerCase() === category
    );
  }

  return deviceList;
};

//Upgrade Section

export const getUpgradeBoosterList = (productList: any, category: string) => {
  let lists = [];
  if (category === CATEGORY.BROADBAND.toLocaleLowerCase()) {
    lists = productList?.filter(
      (it: any) =>
        it.type === PACKAGES &&
        it.categories?.toLocaleLowerCase() === category &&
        it.subCategories?.toLocaleLowerCase() === PACKAGES_LIST.BB_UPGRADE.toLocaleLowerCase()
    );
  }

  return lists;
};

//Addons Section
const isAdded = (id: any, cartProducts: any) => {
  return cartProducts.some((it: any) => it.productId === id);
};

export const getAddonsPackageBasedOnCategory = (
  productList: any,
  category: string,
  cartProducts: any
) => {
  let addonsList = [];

  if (category === CATEGORY.TV.toLocaleLowerCase()) {
    addonsList = productList?.filter(
      (product: any) =>
        product.type === PACKAGES &&
        product.categories.toLocaleLowerCase() === category &&
        !isAdded(product.id, cartProducts)
    );
  } else if (category === CATEGORY.BROADBAND.toLocaleLowerCase()) {
    addonsList = productList?.filter(
      (product: any) =>
        [PACKAGES_LIST.BB_ADDONS, PACKAGES_LIST.BB_ADDONS_VOICE].includes(product.subCategories) &&
        product.type === PACKAGES &&
        product.categories.toLocaleLowerCase() === category
    );
  } else if (category === CATEGORY.TV_BROADBAND.toLocaleLowerCase()) {
    addonsList = [
      ...(productList ?? []).filter(
        (product: any) =>
          product.type === PACKAGES &&
          CATEGORY.TV.toLocaleLowerCase() === product.categories.toLocaleLowerCase() &&
          !isAdded(product.id, cartProducts)
      ),
      ...(productList ?? []).filter(
        (product: any) =>
          [PACKAGES_LIST.BB_ADDONS, PACKAGES_LIST.BB_ADDONS_VOICE].includes(
            product.subCategories
          ) &&
          product.type === PACKAGES &&
          CATEGORY.BROADBAND.toLocaleLowerCase() === product.categories.toLocaleLowerCase()
      ),
    ];
  }

  return addonsList;
};

//Sky Home Phone Addons list

export const getSkyHomePhoePlanList = (productList: any, category: string) => {
  const packageName = PACKAGES_LIST.PACKAGE_BB_ADDONS_VOICE;
  return (
    productList?.filter(
      (product: any) =>
        packageName.toLocaleLowerCase() === product?.type?.toLocaleLowerCase() &&
        product?.categories?.toLocaleLowerCase() === category.toLocaleLowerCase()
    ) || []
  );
};

export const getSkyHomeProduct = (productList: any, category: string) => {
  return productList?.filter(
    (product: any) =>
      product.subCategories === PACKAGES_LIST.BB_ADDONS_VOICE &&
      product.type === PACKAGES &&
      product.categories.toLocaleLowerCase() === category.toLocaleLowerCase()
  );
};

export const getSkyHomePhonePackages = (products: any) => {
  return products?.filter(
    (it: any) =>
      it.subCategory === PACKAGES_LIST.BB_ADDONS_VOICE &&
      (it.type === PACKAGES ||
        (it.type === PACKAGES_LIST.PACKAGE_BB_ADDONS_VOICE &&
          it?.category?.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()))
  );
};

export const selectedWifiRouterFromCart = (products: any) => {
  return products?.some(
    (it: any) =>
      it.type === HARDWARE &&
      it?.categories?.toLocaleLowerCase() !== CATEGORY.TV.toLocaleLowerCase()
  );
};

export const getMyOwnEquipmentProduct = (productList: any) => {
  return productList.length > 0
    ? productList.find((item: any) => item.sku === ADDITIONAL_SKUS_LIST[2])
    : null;
};

export const getProductBasedOnSku = (productList: any, sku: string) => {
  return productList.length > 0
    ? productList.find((item: any) => item?.kkProductInfo?.sku === sku) || null
    : null;
};

export const getRouterProductItem = (productList: any) => {
  const bbHardware = 'BB-Hardware';
  return productList.length > 0
    ? productList.find(
        (item: any) =>
          item?.subCategories?.toLocaleLowerCase() === bbHardware.toLocaleLowerCase() &&
          item?.type?.toLocaleLowerCase() === HARDWARE.toLocaleLowerCase()
      ) || null
    : null;
};

export const getSequentialOrderForHomePhonePackages = (productList: any[]) => {
  if (!Array.isArray(productList)) return [];

  return productList.slice().sort((a: any, b: any) => {
    const skuA = Number.parseInt(a?.kkProductInfo?.sku || '0', 10);
    const skuB = Number.parseInt(b?.kkProductInfo?.sku || '0', 10);
    return skuA - skuB;
  });
};

export const checkProductandCouponIsExistorNot = (
  coupons: T_Coupon[],
  products: productItem[],
  productList: any[]
) => {
  const couponLists = JSON.parse(JSON.stringify(coupons));

  const freeMappingProductSkuIds = productSkuIdList(PRODUCT_SKU_PACKAGE_NAME.SKY_BUNDLES);
  const filteredLists = getPackagesBasedOnCategory(products, CATEGORY.TV.toLocaleLowerCase());

  const isSkuPresent = filteredLists.some((item: any) =>
    freeMappingProductSkuIds.map(String).includes(item.sku)
  );

  const allSkuPresent = freeMappingProductSkuIds.every(id =>
    filteredLists.some((item: any) => Number(item.sku) === id)
  );

  const isMovieProductIsExist = filteredLists.some(
    (item: any) => item.sku === productSkuIds.movies.primary
  );

  const product = isSkuPresent
    ? productList.find(
        (item: any) => item.kkProductInfo.sku === String(freeMappingProductSkuIds[0])
      )
    : undefined;

  let couponCheckList: string[] = [];

  const fetchAndStoreCoupon = async (couponCode: string) => {
    if (!couponLists.some((item: any) => item.couponCode === couponCode)) {
      couponCheckList.push(couponCode);
    }
  };

  if (allSkuPresent) {
    if (isMovieProductIsExist) {
      // Fetch movie coupon if not already present
      fetchAndStoreCoupon(BUNDLE_MOVIE_COUPON);

      // Fetch bundle coupon if not already present
      fetchAndStoreCoupon(product.productKkInfo.couponCode);
    } else {
      // Fetch bundle coupon if not already present
      fetchAndStoreCoupon(product.productKkInfo.couponCode);
    }
  } else if (isSkuPresent) {
    // Fetch bundle coupon if not already present
    fetchAndStoreCoupon(product.productKkInfo.couponCode);
  }

  if (!allSkuPresent) {
    couponCheckList = couponCheckList.filter(couponCode => couponCode !== BUNDLE_MOVIE_COUPON);
  }

  if (!allSkuPresent && !isSkuPresent) {
    const product = removeBundleCoupon(products, productList);

    // Only filter if the product exists and has a valid removeCouponCode
    if (product?.productKkInfo?.removeCouponCode) {
      couponCheckList = couponCheckList.filter(
        couponCode => couponCode !== product.productKkInfo.removeCouponCode
      );
    }
  }

  return {
    couponCheckList,
    allSkuPresent,
    isMovieProductIsExist,
    isSkuPresent,
  };
};

export const getServiceCodeAndCampaignsBasedOnJourneyType = (
  productType: string,
  category: string,
  couponLists: T_Coupon[],
  campaign: string[] | null = [], // Default to an empty array if campaign is null or undefined
  serviceCode: string[] | null = [] // Default to an empty array if serviceCode is null or undefined
): FilteredResults => {
  // Fallback for null or undefined inputs
  const safeCampaign = campaign || [];
  const safeServiceCode = serviceCode || [];

  // Utility function to filter out items ending with 'BB'
  const filterBB = (arr: string[]) => arr.filter(item => item.endsWith('BB'));
  const filterNotBB = (arr: string[]) => arr.filter(item => !item.endsWith('BB'));

  // Define broadband-related campaigns and service codes
  const broadbandCampaign = filterBB(safeCampaign);
  const broadbandServiceCode = filterBB(safeServiceCode);

  let filteredCampaign: string[] = [];
  let filteredServiceCode: string[] = [];
  let filteredCouponList: T_Coupon[] = [];

  // Handle different categories
  switch (category.toLowerCase()) {
    case CATEGORY.TV.toLowerCase():
      // For 'TV', exclude anything that ends with 'BB'
      filteredCampaign = filterNotBB(safeCampaign);
      filteredServiceCode = filterNotBB(safeServiceCode);
      break;

    case CATEGORY.BROADBAND.toLowerCase():
      // For 'BROADBAND', keep only those ending with 'BB'
      filteredCampaign = broadbandCampaign;
      filteredServiceCode = broadbandServiceCode;
      break;

    case CATEGORY.TV_BROADBAND.toLowerCase():
      // For 'TV+BROADBAND', logic depends on product type
      if (productType === VoucherProductTypeEnums.DTH) {
        const filteredCoupon = couponLists.filter((coupon: { couponCode: string }) =>
          coupon.couponCode.endsWith('BB')
        );
        filteredCouponList = [...filteredCoupon];
        filteredCampaign = filterNotBB(safeCampaign);
        filteredServiceCode = filterNotBB(safeServiceCode);
      } else if (productType === VoucherProductTypeEnums.BROADBAND) {
        const filteredCoupon = couponLists.filter(
          (coupon: { couponCode: string }) => !coupon.couponCode.endsWith('BB')
        );
        filteredCouponList = [...filteredCoupon];
        filteredCampaign = broadbandCampaign;
        filteredServiceCode = broadbandServiceCode;
      } else {
        filteredCampaign = safeCampaign;
        filteredServiceCode = safeServiceCode;
      }
      break;

    default:
      break;
  }

  // Return the filtered results
  return {
    filteredCampaign,
    filteredServiceCode,
    filteredCouponList,
  };
};

export const enabledATLOffer = (category: string, productType: string, productCategory: string) => {
  if (category.toLowerCase() === CATEGORY.TV_BROADBAND.toLowerCase()) {
    if (
      productType.toLowerCase() === VoucherProductTypeEnums.DTH.toLowerCase() &&
      productCategory.toLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()
    ) {
      return true;
    } else if (
      productType.toLowerCase() === VoucherProductTypeEnums.BROADBAND.toLowerCase() &&
      productCategory.toLowerCase() === CATEGORY.TV.toLocaleLowerCase()
    ) {
      return true;
    } else if (
      productType.toLowerCase() === VoucherProductTypeEnums.DTH_AND_BROADBAND.toLowerCase() &&
      productCategory.toLowerCase() === CATEGORY.TV.toLocaleLowerCase()
    ) {
      return false;
    }
  }
  return false;
};

export const getSelectedDeviceType = (products: productItem[]) => {
  const selectedDeviceProduct = products?.find(
    (it: any) => it.type === HARDWARE && it.category?.toLowerCase() === CATEGORY.TV.toLowerCase()
  );

  if (!selectedDeviceProduct) return 'Any';

  const name = getDeviceType(selectedDeviceProduct.sku);
  return name === PRODUCT_SKU_PACKAGE_NAME.SKY_BOX ? DEVICE_TYPE.BOX : DEVICE_TYPE.POD;
};

export const removeBundleCoupon = (products: productItem[], productList: any) => {
  const filteredLists = getPackagesBasedOnCategory(products, CATEGORY.TV.toLocaleLowerCase());

  const updateProduct = filteredLists.filter((it: any) => it.sku !== productSkuIds.movies.primary);
  let bundleProduct: any = null;
  if (updateProduct.length === 1) {
    // Only one item is expected to match
    bundleProduct = productList.find(
      (item: any) => item.kkProductInfo.sku === updateProduct[0].sku
    );
  }
  return bundleProduct;
};
